import React from 'react';
import classNames from 'classnames';
import { observer, inject } from 'mobx-react';
import { createStyles, WithStyles, withStyles } from '@mui/styles';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Theme, Typography, Button } from '@mui/material';

import { WizardModel } from 'models/kiosk/WizardModel';
import { Services } from 'helpers/constants/Services';
import { Billpay } from 'models/kiosk/Billpay';
import { ReceiptStep } from 'interfaces';
import StoreModel from 'models/Index';
import { goApplicationSelect, goHome } from 'sdk/InternalSdk';

interface FarewellStepProps
    extends WithStyles<typeof styles>,
        WithTranslation {}
interface FarewellStepPropsInjected extends FarewellStepProps {
    wizard: WizardModel;
    billpay: Billpay;
    Store: StoreModel;
}
const styles = (theme: Theme) =>
    createStyles({
        root: {
            padding: '0 60px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
        },
        body: {
            marginTop: '5vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        transactionTitle: {
            color: theme.palette.primary.main,
            fontSize: '3.5rem',
        },
        transactionSuccessful: {
            textAlign: 'center',
            marginTop: '64px',
        },
        buttons: {
            marginTop: '30px',

            '& button': {
                height: '104px',
                width: '504px',
                marginTop: '72px',
                display: 'block',
            },
        },
        receiptStep: {
            color: theme.palette.secondary.main,
            fontSize: '2.25rem',
            fontWeight: 'normal',
        },
        middleFont: {
            fontSize: '2.16rem',
        },
        transactionError: {
            textAlign: 'center',
            marginTop: '100px',
        },
        transactionErrorText: {
            fontSize: '1.25em',
            fontWeight: 'bold',
        }
    });

@inject(Services.STORE, Services.PAYMENT_WIZARD, Services.BILLPAY)
@observer
class FarewellStep extends React.Component<FarewellStepProps, any> {
    get injected() {
        return this.props as FarewellStepPropsInjected;
    }

    componentDidMount() {
        const { Store } = this.injected;

        Store.showHomeButton();
    }

    render() {
        const { classes, t } = this.props;
        const { billpay } = this.injected;
        const { step, generateFailed } = billpay.receipts;

        return (
            <div className={classes.root}>
                <div className={classes.body}>
                    <Typography
                        className={classes.transactionTitle}
                        variant='h2'
                    >
                        {t('Thank You!')}
                    </Typography>

                    {!generateFailed && (
                        <div className={classes.transactionSuccessful}>
                            <Typography
                                className={classes.receiptStep}
                                variant='h3'
                            >
                                {step === ReceiptStep.Print &&
                                    t('Your receipt was successfully printed')}
                                {step === ReceiptStep.Email &&
                                    t('Your receipt was successfully sent')}
                                {step === ReceiptStep.PrintAndEmail &&
                                    t(
                                        'Your receipt was successfully sent and printed'
                                    )}
                            </Typography>
                        </div>
                    )}

                    <div className={classes.transactionSuccessful}>
                        <Typography className={classes.middleFont} variant='h4'>
                            {t('Would you like to make another payment?')}
                        </Typography>
                    </div>
                    <div
                        className={classNames(
                            classes.transactionSuccessful,
                            classes.buttons
                        )}
                    >
                        <Button
                            variant='primary'
                            onClick={this.startNewPayment.bind(this)}
                        >
                            <Typography
                                className={classes.middleFont}
                                variant='button'
                            >
                                {t('Yes')}
                            </Typography>
                        </Button>
                        <Button
                            variant='secondary'
                            onClick={(_) => {
                                billpay.reset();
                                goHome();
                            }}
                        >
                            <Typography
                                className={classes.middleFont}
                                variant='button'
                            >
                                {t('No')}
                            </Typography>
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    startNewPayment() {
        const { billpay } = this.injected;

        billpay.reset();
        billpay.resetTraceId();

        goApplicationSelect();
    }
}

export default withTranslation()(withStyles(styles)(FarewellStep));
